import { useState } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import usePageMeta from '@/hooks/usePageMeta';

const SCOPE = [
  { icon: 'ri-newspaper-line', title: '影视资讯聚合', desc: '电影、电视剧最新动态与行业热点，第一时间掌握。' },
  { icon: 'ri-filter-3-line', title: '分类筛选浏览', desc: '按地区、年份、类型多维筛选，快速找到你想看的作品。' },
  { icon: 'ri-file-info-line', title: '影视详情资料', desc: '基础信息、剧情简介与关联资讯，一页看全一部作品。' },
  { icon: 'ri-quill-pen-line', title: '影评攻略专题', desc: '深度影评与观影攻略，带你看懂作品背后的故事。' },
];

const FORM_ADDR = 'https://readdy.ai/api/form/db88ilk6oi3g3tv8uju0';

export default function AboutPage() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  usePageMeta({
    title: '关于我们 - 御宅屋影视资讯平台',
    description: '了解御宅屋影视娱乐综合站的定位与内容范围，并通过反馈表单与我们联系。',
    keywords: '关于御宅屋,影视平台介绍,联系我们,御宅屋反馈',
  });

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      form.reset();
      return;
    }

    setStatus('submitting');
    setFormError('');

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      const str = String(value).trim();
      if (str) payload.append(key, str);
    });

    try {
      const response = await fetch(FORM_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isOk = response.ok && parsed?.code === 'OK';

      if (isOk) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setFormError(
          serverMsg && serverMsg.toLowerCase().includes('spam')
            ? '提交内容被系统判定为异常，请修改后重试'
            : serverMsg || '提交失败，请稍后重试',
        );
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，提交失败，请稍后重试');
    }
  };

  return (
    <div className="w-full px-4 md:px-8 pb-16">
      <div className="pt-2">
        <Breadcrumb items={[{ label: '关于我们' }]} />
      </div>

      <div className="rounded-lg bg-gradient-to-r from-primary-900/60 to-background-100 border border-background-200/70 p-6 md:p-10 mb-10">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
          关于御宅屋
        </h1>
        <p className="mt-4 text-sm md:text-base text-foreground-600 max-w-3xl leading-[1.9]">
          御宅屋是一个专注于影视娱乐资讯聚合的内容平台，面向喜爱影视内容、习惯追剧看电影的广大影迷与娱乐资讯用户。我们希望用清晰的结构与丰富的内容，让你在最短的时间里，获取最新影视资讯、浏览影片推荐、查看影视详情。
        </p>
        <p className="mt-3 text-sm md:text-base text-foreground-500 max-w-3xl leading-[1.9]">
          本站目前为内容展示与体验演示站，所有影视资料与资讯均为演示数据，不提供在线播放、下载或支付功能。
        </p>
      </div>

      <section className="mb-12">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-1 h-6 rounded-full bg-accent-500"></span>
          <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950">
            我们提供的内容范围
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {SCOPE.map((item) => (
            <div
              key={item.title}
              className="rounded-lg bg-background-100 border border-background-200/70 p-5 hover:border-accent-500/50 transition-colors"
            >
              <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-accent-500/15 text-accent-400 border border-accent-500/30">
                <i className={`${item.icon} text-xl`}></i>
              </span>
              <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">
                {item.title}
              </h3>
              <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8">
        <section className="lg:col-span-2">
          <div className="rounded-lg bg-background-100 border border-background-200/70 p-6 h-full">
            <h2 className="font-heading text-lg font-bold text-foreground-950 mb-5 flex items-center gap-2">
              <span className="w-1 h-5 rounded-full bg-accent-500"></span>
              联系我们
            </h2>
            <div className="flex flex-col gap-4">
              <div className="flex items-start gap-3">
                <span className="w-9 h-9 flex items-center justify-center rounded-md bg-secondary-500/15 text-secondary-300 border border-secondary-500/30 shrink-0">
                  <i className="ri-mail-line"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-500">联系邮箱</p>
                  <p className="text-sm text-foreground-900">contact@yuzhaiwu-demo.com</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-9 h-9 flex items-center justify-center rounded-md bg-secondary-500/15 text-secondary-300 border border-secondary-500/30 shrink-0">
                  <i className="ri-time-line"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-500">内容更新</p>
                  <p className="text-sm text-foreground-900">每日跟踪影视资讯动态</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-9 h-9 flex items-center justify-center rounded-md bg-secondary-500/15 text-secondary-300 border border-secondary-500/30 shrink-0">
                  <i className="ri-chat-3-line"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-500">意见反馈</p>
                  <p className="text-sm text-foreground-900">欢迎通过右侧表单告诉我们你的想法</p>
                </div>
              </div>
            </div>
            <div className="mt-6 pt-5 border-t border-background-200/70">
              <p className="text-xs text-foreground-500 leading-relaxed">
                如果你有想看的影视作品推荐，或者发现内容有误，都欢迎随时联系我们。你的每一条建议，都会让御宅屋变得更好。
              </p>
              <Link
                to="/special"
                className="mt-4 inline-flex items-center gap-1 text-sm text-accent-400 hover:underline"
              >
                看看我们的专题内容
                <i className="ri-arrow-right-s-line"></i>
              </Link>
            </div>
          </div>
        </section>

        <section className="lg:col-span-3">
          <div className="rounded-lg bg-background-100 border border-background-200/70 p-6 md:p-7">
            <h2 className="font-heading text-lg font-bold text-foreground-950 mb-1">
              意见反馈
            </h2>
            <p className="text-sm text-foreground-500 mb-6">
              填写下面的表单，把你的建议或想法告诉我们（本表单为演示提交，会真实记录反馈内容）。
            </p>

            {status === 'success' ? (
              <div className="flex flex-col items-center justify-center py-14 rounded-lg bg-background-50/60 border border-accent-500/40">
                <span className="w-14 h-14 flex items-center justify-center rounded-full bg-accent-500/15 text-accent-400 border border-accent-500/40">
                  <i className="ri-check-line text-2xl"></i>
                </span>
                <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">
                  反馈已提交，感谢你的建议！
                </h3>
                <p className="mt-1.5 text-sm text-foreground-500">我们会认真阅读每一条留言</p>
                <button
                  type="button"
                  onClick={() => setStatus('idle')}
                  className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-background-300/70 text-foreground-800 text-sm hover:border-accent-500/60 hover:text-accent-400 transition-colors cursor-pointer whitespace-nowrap"
                >
                  继续反馈
                </button>
              </div>
            ) : (
              <form
                id="yuzhaiwu-feedback-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="flex flex-col gap-4"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="fb-name" className="block text-sm text-foreground-700 mb-2">
                      你的称呼
                    </label>
                    <input
                      id="fb-name"
                      name="name"
                      type="text"
                      required
                      placeholder="请输入你的称呼"
                      className="w-full px-4 py-2.5 rounded-md bg-background-50/70 border border-background-300/60 text-sm text-foreground-900 placeholder:text-foreground-500 outline-none focus:border-accent-500/70 transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="fb-email" className="block text-sm text-foreground-700 mb-2">
                      邮箱
                    </label>
                    <input
                      id="fb-email"
                      name="email"
                      type="email"
                      required
                      placeholder="用于接收回复"
                      className="w-full px-4 py-2.5 rounded-md bg-background-50/70 border border-background-300/60 text-sm text-foreground-900 placeholder:text-foreground-500 outline-none focus:border-accent-500/70 transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="fb-topic" className="block text-sm text-foreground-700 mb-2">
                    反馈类型
                  </label>
                  <select
                    id="fb-topic"
                    name="topic"
                    defaultValue="内容建议"
                    className="w-full px-4 py-2.5 rounded-md bg-background-50/70 border border-background-300/60 text-sm text-foreground-900 outline-none focus:border-accent-500/70 transition-colors cursor-pointer"
                  >
                    <option value="内容建议">内容建议</option>
                    <option value="影视推荐">影视推荐</option>
                    <option value="问题反馈">问题反馈</option>
                    <option value="合作洽谈">合作洽谈</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="fb-message" className="block text-sm text-foreground-700 mb-2">
                    留言内容
                  </label>
                  <textarea
                    id="fb-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="请写下你的建议或想说的内容（最多 500 字）"
                    className="w-full px-4 py-2.5 rounded-md bg-background-50/70 border border-background-300/60 text-sm text-foreground-900 placeholder:text-foreground-500 outline-none focus:border-accent-500/70 transition-colors resize-none"
                  />
                </div>

                <input
                  type="text"
                  name="website_alt"
                  className="form-anti-bot-field"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                {status === 'error' && formError && (
                  <p className="text-sm text-accent-400 bg-accent-500/10 border border-accent-500/30 rounded-md px-4 py-2.5">
                    <i className="ri-error-warning-line mr-1.5"></i>
                    {formError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="self-start inline-flex items-center gap-2 px-6 py-3 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status === 'submitting' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      提交中...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line"></i>
                      提交反馈
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}